/**
 * SegmentManager.js
 * 
 * This module manages the creation and handling of segmented batches for the virtual deck system.
 * It provides functions for creating batches, managing segments, and minting cards.
 */

import { 
  generateSecureSeed, 
  generateSecureSalt, 
  generateSegmentHash, 
  shufflePrizeList,
  summarizePrizeDistribution
} from './VirtualDeckGenerator';
import {
  storeSegmentHashOnChain,
  revealSegmentOnChain
} from './SolanaIntegration';

/**
 * Generates a unique batch ID
 * @returns {string} A unique batch ID
 */
export const generateBatchId = () => {
  return 'batch_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
};

/**
 * Generates a unique segment ID
 * @param {string} batchId - The batch ID
 * @param {number} segmentIndex - The segment index
 * @returns {string} A unique segment ID
 */
export const generateSegmentId = (batchId, segmentIndex) => {
  return `${batchId}_segment_${segmentIndex}`;
};

/**
 * Creates a prize list for a segment based on game configuration
 * @param {Object} gameConfig - The game configuration
 * @param {number} segmentIndex - The segment index
 * @param {number} totalSegments - The total number of segments
 * @param {number} segmentSize - The size of this segment
 * @returns {Array} The prize list for this segment
 */
export const createPrizeListForSegment = (gameConfig, segmentIndex, totalSegments, segmentSize) => {
  // This function distributes prizes across segments based on the game configuration
  const prizeList = [];
  
  // If game config has bounty pool tiers, use them to create the prize list
  if (gameConfig.bountyPool && gameConfig.bountyPool.tiers) {
    gameConfig.bountyPool.tiers.forEach(tier => {
      if (tier.prizes) {
        tier.prizes.forEach(prize => {
          // Calculate how many of this prize should be in this segment
          // This ensures prizes are distributed fairly across segments
          const totalCount = prize.count;
          const segmentCount = Math.floor(totalCount / totalSegments);
          const remainder = totalCount % totalSegments;
          
          // Add extra prizes to early segments if there's a remainder
          const countForThisSegment = segmentCount + (segmentIndex < remainder ? 1 : 0);
          
          // Add prizes to the list
          for (let i = 0; i < countForThisSegment; i++) {
            prizeList.push({
              amount: prize.amount,
              value: prize.amount >= 1000 ? `${prize.amount / 1000}K` : prize.amount.toString(),
              isWinning: prize.amount > 0,
              tier: tier.name
            });
          }
        });
      }
    });
  } else {
    // Fallback if no bounty pool is defined
    // Create a simple prize distribution based on win probability
    const winRate = gameConfig.winProbability || 0.3;
    const winCount = Math.floor(segmentSize * winRate);
    
    // Add winning prizes
    for (let i = 0; i < winCount; i++) {
      const prizeAmount = gameConfig.prizes && gameConfig.prizes.length > 0 
        ? gameConfig.prizes[Math.floor(Math.random() * gameConfig.prizes.length)]
        : 200;
        
      prizeList.push({
        amount: prizeAmount,
        value: prizeAmount.toString(),
        isWinning: true
      });
    }
    
    // Add non-winning entries
    for (let i = 0; i < segmentSize - winCount; i++) {
      prizeList.push({
        amount: 0,
        value: '0',
        isWinning: false
      });
    }
  }
  
  // If we have too many prizes, trim the list
  if (prizeList.length > segmentSize) {
    prizeList.length = segmentSize;
  }
  
  // If we have too few prizes, add non-winning entries
  while (prizeList.length < segmentSize) {
    prizeList.push({
      amount: 0,
      value: '0',
      isWinning: false
    });
  }
  
  return prizeList;
};

/**
 * Creates a segmented batch for a game
 * @param {Object} gameConfig - The game configuration
 * @param {number} totalCards - The total number of cards in the batch
 * @param {number} segmentSize - The size of each segment
 * @returns {Object} The created batch
 */
export const createSegmentedBatch = (gameConfig, totalCards, segmentSize = 100) => {
  const batchId = generateBatchId();
  const segments = [];
  const totalSegments = Math.ceil(totalCards / segmentSize);
  
  for (let i = 0; i < totalSegments; i++) {
    const segmentCards = Math.min(segmentSize, totalCards - i * segmentSize);
    const seed = generateSecureSeed();
    const salt = generateSecureSalt();
    const prizeList = createPrizeListForSegment(gameConfig, i, totalSegments, segmentCards);
    const segmentHash = generateSegmentHash(prizeList, seed, salt);
    
    segments.push({
      id: generateSegmentId(batchId, i),
      index: i,
      seed,
      salt,
      segmentHash,
      cardCount: segmentCards,
      nextCardIndex: 0,
      isComplete: false,
      prizeList,
      prizeDistribution: summarizePrizeDistribution(prizeList)
    });
  }
  
  // Store the batch data
  const batchData = {
    id: batchId,
    gameId: gameConfig.id,
    gameName: gameConfig.name,
    createdAt: new Date().toISOString(),
    totalCards,
    segmentSize,
    totalSegments,
    segments: segments.map(s => ({
      id: s.id,
      index: s.index,
      segmentHash: s.segmentHash,
      cardCount: s.cardCount,
      nextCardIndex: 0,
      isComplete: false,
      prizeDistribution: s.prizeDistribution
    }))
  };
  
  // Store the batch data in localStorage
  localStorage.setItem(`batch_${batchId}`, JSON.stringify(batchData));
  
  // Store each segment separately (with sensitive data)
  segments.forEach(segment => {
    localStorage.setItem(`segment_${segment.id}`, JSON.stringify(segment));
  });
  
  // Store segment hashes on-chain (simulated for devnet)
  // In a production environment, you would use a real wallet
  const simulatedWallet = { publicKey: 'simulated_wallet' };
  
  // Store each segment hash on-chain
  segments.forEach(async segment => {
    try {
      const signature = await storeSegmentHashOnChain(
        batchId,
        segment.id,
        segment.segmentHash,
        simulatedWallet
      );
      
      console.log(`Stored segment hash on-chain: ${signature}`);
      
      // Store the transaction signature in the segment
      const updatedSegment = { ...segment, onChainSignature: signature };
      localStorage.setItem(`segment_${segment.id}`, JSON.stringify(updatedSegment));
    } catch (error) {
      console.error(`Error storing segment hash on-chain: ${error.message}`);
    }
  });
  
  return batchData;
};

/**
 * Gets a batch by ID
 * @param {string} batchId - The batch ID
 * @returns {Object} The batch data
 */
export const getBatch = (batchId) => {
  const batchData = localStorage.getItem(`batch_${batchId}`);
  return batchData ? JSON.parse(batchData) : null;
};

/**
 * Gets a segment by ID
 * @param {string} segmentId - The segment ID
 * @returns {Object} The segment data
 */
export const getSegment = (segmentId) => {
  const segmentData = localStorage.getItem(`segment_${segmentId}`);
  return segmentData ? JSON.parse(segmentData) : null;
};

/**
 * Updates a segment
 * @param {Object} segment - The segment data to update
 */
export const updateSegment = (segment) => {
  localStorage.setItem(`segment_${segment.id}`, JSON.stringify(segment));
  
  // Also update the segment in the batch
  const batchId = segment.id.split('_segment_')[0];
  const batch = getBatch(batchId);
  if (batch) {
    const segmentIndex = batch.segments.findIndex(s => s.id === segment.id);
    if (segmentIndex !== -1) {
      batch.segments[segmentIndex] = {
        id: segment.id,
        index: segment.index,
        segmentHash: segment.segmentHash,
        cardCount: segment.cardCount,
        nextCardIndex: segment.nextCardIndex,
        isComplete: segment.isComplete,
        prizeDistribution: segment.prizeDistribution
      };
      localStorage.setItem(`batch_${batchId}`, JSON.stringify(batch));
    }
  }
};

/**
 * Gets the active segment for a batch
 * @param {string} batchId - The batch ID
 * @returns {Object} The active segment
 */
export const getActiveSegment = (batchId) => {
  const batch = getBatch(batchId);
  if (!batch) return null;
  
  const activeSegmentInfo = batch.segments.find(s => !s.isComplete);
  if (!activeSegmentInfo) return null;
  
  return getSegment(activeSegmentInfo.id);
};

/**
 * Mints a card from a batch
 * @param {string} batchId - The batch ID
 * @param {Object} nftManager - Optional NFTManager instance for minting NFTs
 * @param {string} playerAddress - Optional player wallet address for NFT minting
 * @returns {Promise<Object>} The minted card
 */
export const mintCardFromBatch = async (batchId, nftManager = null, playerAddress = null) => {
  // Get the active segment
  const segment = getActiveSegment(batchId);
  if (!segment) {
    throw new Error('No active segment found for this batch');
  }
  
  // Get the current card index
  const cardIndex = segment.nextCardIndex;
  if (cardIndex >= segment.cardCount) {
    throw new Error('All cards in this segment have been minted');
  }
  
  // Get the prize for this card using the shuffled prize list
  const shuffled = shufflePrizeList(segment.prizeList, segment.seed, segment.salt);
  const prize = shuffled[cardIndex];
  
  // Update the segment
  segment.nextCardIndex++;
  
  // Check if the segment is now complete
  if (segment.nextCardIndex >= segment.cardCount) {
    segment.isComplete = true;
    
    // Reveal the seed and salt on-chain
    try {
      // In a production environment, you would use a real wallet
      const simulatedWallet = { publicKey: 'simulated_wallet' };
      
      const signature = await revealSegmentOnChain(
        segment.id.split('_segment_')[0], // batchId
        segment.id,
        segment.seed,
        segment.salt,
        simulatedWallet
      );
      
      console.log(`Revealed segment seed and salt on-chain: ${signature}`);
      
      // Store the reveal transaction signature in the segment
      segment.onChainRevealSignature = signature;
    } catch (error) {
      console.error(`Error revealing segment on-chain: ${error.message}`);
    }
    
    console.log(`Segment ${segment.id} is complete. Seed: ${segment.seed}, Salt: ${segment.salt}`);
  }
  
  // Save the updated segment
  updateSegment(segment);
  
  // Create card data
  const cardData = {
    batchId,
    segmentId: segment.id,
    cardIndex,
    prize,
    isSegmentComplete: segment.isComplete,
    verification: {
      segmentHash: segment.segmentHash,
      segmentIndex: segment.index,
      cardIndex
    },
    createdAt: Date.now()
  };
  
  // If NFT Manager is provided, mint an NFT
  if (nftManager && playerAddress) {
    try {
      // Get the batch to access game config
      const batch = getBatch(batchId);
      
      // Create NFT metadata
      const metadata = {
        name: `${batch.gameConfig.name || 'Scratch Card'} #${cardData.batchId}-${cardData.cardIndex}`,
        symbol: batch.gameConfig.symbol || 'SCRATCH',
        description: `${batch.gameConfig.description || 'Palm Heist Scratch Card'} from batch ${batchId}`,
        image: batch.gameConfig.cardImage || 'https://via.placeholder.com/500x300?text=Scratch+Card',
        attributes: [
          { trait_type: 'Game', value: batch.gameConfig.name || 'Scratch Card' },
          { trait_type: 'Batch', value: batchId },
          { trait_type: 'Segment', value: segment.id },
          { trait_type: 'Card Index', value: cardIndex.toString() },
          { trait_type: 'Prize', value: prize.toString() }
        ],
        gameData: {
          batchId,
          segmentId: segment.id,
          cardIndex,
          prize,
          revealed: false
        }
      };
      
      // Mint the NFT
      const nft = await nftManager.mintGameCard(
        batch.gameConfig.id || 'scratch-card',
        playerAddress,
        metadata
      );
      
      // Add NFT data to the card
      cardData.nft = {
        id: nft.id,
        mintAddress: nft.mintAddress,
        owner: nft.owner,
        metadata: nft.metadata
      };
      
      console.log(`Minted NFT for card: ${nft.mintAddress}`);
    } catch (error) {
      console.error('Error minting NFT:', error);
      // Continue without NFT - the card is still valid
    }
  }
  
  return cardData;
};

/**
 * Gets or creates a batch for a game
 * @param {Object} gameConfig - The game configuration
 * @param {number} totalCards - The total number of cards in the batch
 * @param {number} segmentSize - The size of each segment
 * @returns {Object} The batch data
 */
export const getOrCreateBatch = (gameConfig, totalCards = 1000, segmentSize = 100) => {
  // Look for an existing batch for this game
  const existingBatches = Object.keys(localStorage)
    .filter(key => key.startsWith('batch_'))
    .map(key => JSON.parse(localStorage.getItem(key)))
    .filter(batch => batch.gameId === gameConfig.id);
  
  // Find a batch with an active segment
  const batchWithActiveSegment = existingBatches.find(batch => 
    batch.segments.some(segment => !segment.isComplete)
  );
  
  if (batchWithActiveSegment) {
    return batchWithActiveSegment;
  }
  
  // Create a new batch
  return createSegmentedBatch(gameConfig, totalCards, segmentSize);
};
